import { useTranslations } from 'next-intl';
import { useCallback, useMemo } from 'react';
import {
  PaginationEllipsis,
  PaginationLink,
  PaginationNavLink,
  Pagination as StyledPagination,
} from '@//components/Pagination';
import type { ElasticSearchResponseDocumentListPagination } from '@//ElasticSearch';
import { createPaginationWithEllipsis } from '@//utils/pagination';
import useUpdateVehiclesSearchUrl from '@//utils/vehicles-search/useUpdateVehiclesSearchUrl';
import { facetParamsToPath, sanitizeParams } from '@//utils/vehicles-search/utils';

const resolveHref = (rootUrl: string, q?: string) => {
  const params = sanitizeParams(new URLSearchParams(q), { keepOffset: true });
  const hasOffset = params.has('offset');
  const hasQ = params.has('q');
  if (hasOffset || hasQ) {
    return `${rootUrl}${params.toString() ? `?${params}` : ''}`;
  }

  const pretty = facetParamsToPath(params);

  const paramsAsString = params.toString();
  const href = pretty
    ? `${rootUrl}/${pretty}`
    : paramsAsString
      ? `?${paramsAsString}`
      : window.location.pathname;

  return href;
};

interface PaginationProps {
  rootUrl: string;
  pagination?: ElasticSearchResponseDocumentListPagination;
}

const Pagination = ({ rootUrl, pagination }: PaginationProps) => {
  const t = useTranslations('other.pagination');
  const updateUrl = useUpdateVehiclesSearchUrl(rootUrl);

  const onClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, queryString: string) => {
      e.preventDefault();
      e.stopPropagation();
      updateUrl(sanitizeParams(queryString, { keepOffset: true }), {
        scrollTop: true,
      });

      // Focus the search results grid after navigation with proper timing
      // First blur the current element to remove focus from pagination button
      (e.target as HTMLElement).blur();

      requestAnimationFrame(() => {
        setTimeout(() => {
          const searchResultsElement = document.getElementById('search-results');
          if (searchResultsElement) {
            searchResultsElement.focus();
            // Ensure visual focus indicator is visible
            searchResultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 300);
      });
    },
    [updateUrl],
  );

  const prevQuery = pagination?.previousPage?.query;

  const nextQuery = pagination?.nextPage?.query;

  const paginationItems = useMemo(
    () => createPaginationWithEllipsis(pagination?.pages || []),
    [pagination?.pages],
  );

  if (!pagination) {
    return null;
  }

  return (
    <StyledPagination testId="search-pagination" ariaLabel={t('ariaLabel')} center>
      <PaginationNavLink
        variant="prev"
        href={prevQuery ? resolveHref(prevQuery) : undefined}
        testId="search-pagination-prev"
        onClick={prevQuery ? (e) => onClick(e, prevQuery) : undefined}
      />
      {paginationItems.map((item, index) => {
        if (item.isEllipsis) {
          return (
            <PaginationEllipsis
              key={`ellipsis-${index}`}
              testId={`search-pagination-ellipsis-${index}`}
            />
          );
        }

        return (
          <PaginationLink
            key={item.displayName}
            href={resolveHref(rootUrl, item.query!)}
            onClick={(e) => onClick(e, item.query!)}
            title={t('goTo.title', { page: item.displayName })}
            testId={`search-pagination-page-${item.displayName}`}
            isActive={item.selected}
          >
            {item.displayName}
          </PaginationLink>
        );
      })}
      <PaginationNavLink
        variant="next"
        href={nextQuery ? resolveHref(rootUrl, nextQuery) : undefined}
        testId="search-pagination-next"
        onClick={nextQuery ? (e) => onClick(e, nextQuery) : undefined}
      />
    </StyledPagination>
  );
};

export default Pagination;
